import { useEffect, useMemo, useState } from "react";
import {
  terminalDiagnosticCopy,
  terminalDiagnosticDetails,
} from "@/client/terminal-diagnostics";
import type {
  CatalogDraft,
  DepositItem,
  InstallationSession,
  PosBalance,
  RefundSettings,
  TerminalDiagnostic,
  TerminalResult,
} from "@/client/types";
import { WorkerApi } from "@/client/worker-api";
import { CheckoutPanel } from "@/components/CheckoutPanel";
import { DepositCatalog } from "@/components/DepositCatalog";
import { ReceiptIcon, SettingsIcon } from "@/components/icons";
import { OperationsDialog } from "@/components/OperationsDialog";
import {
  PaymentStatusDialog,
  type PaymentTerminalState,
} from "@/components/PaymentStatusDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import { StatusDialog } from "@/components/StatusDialog";
import { DepositApiClient } from "@/lib/api";
import { createLightspeedClient } from "@/lib/lightspeed";
import {
  RefundOrchestrator,
  type RefundProgressStage,
} from "@/lib/refund-orchestrator";
import { WorkerRequestError } from "@/lib/worker-error";
import type { RefundOperation } from "@/shared/types";

const emptySettings: RefundSettings = {
  environment: "test",
  region: "eu",
  merchantAccount: "",
  terminalId: "",
  saleId: "",
  currency: "EUR",
  maxRefundCents: 0,
  settlementRoute: "",
  apiKey: "",
  webhookHmacKey: "",
  webhookUrl: "",
};

const INSTALLATION_STORAGE_KEY = "lightspeed-deposit.installation.v1";

export function App() {
  const [settings, setSettings] = useState(emptySettings);
  const [items, setItems] = useState<DepositItem[]>([]);
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [paymentStage, setPaymentStage] =
    useState<RefundProgressStage>("preparing");
  const [paymentTerminalState, setPaymentTerminalState] =
    useState<PaymentTerminalState>("checking");
  const [terminalTransactionType, setTerminalTransactionType] = useState<
    "payment" | "refund" | null
  >(null);
  const [session, setSession] = useState<InstallationSession | null>(null);
  const [balance, setBalance] = useState<PosBalance | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showOperations, setShowOperations] = useState(false);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState<string>();
  const [operations, setOperations] = useState<RefundOperation[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string>();
  const [terminalResult, setTerminalResult] = useState<TerminalResult>("idle");
  const [terminalDiagnostic, setTerminalDiagnostic] =
    useState<TerminalDiagnostic | null>(null);
  const [status, setStatus] = useState<{
    title: string;
    message: string;
    detail?: string;
    tone?: "success" | "warning";
  } | null>(null);

  const selectedCount = useMemo(
    () => items.reduce((count, item) => count + item.quantity, 0),
    [items],
  );
  const api = useMemo(
    () => (session ? new WorkerApi(session) : null),
    [session],
  );
  const configurationComplete = Boolean(
    session &&
      settings.merchantAccount.trim() &&
      settings.terminalId.trim() &&
      settings.settlementRoute.trim(),
  );

  useEffect(() => {
    const bridge = globalThis as unknown as { pos_loadData?: unknown };
    if (typeof bridge.pos_loadData !== "function") return;
    void createLightspeedClient()
      .load<InstallationSession>(INSTALLATION_STORAGE_KEY)
      .then((stored) => {
        if (isInstallationSession(stored)) setSession(stored);
      })
      .catch((error: unknown) => {
        setStatus({
          title: "Installation konnte nicht gelesen werden",
          message:
            error instanceof Error ? error.message : "Unbekannter Fehler",
          tone: "warning",
        });
      });
  }, []);

  useEffect(() => {
    if (!isSubmittingRefund || !api) return;
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const pollTerminal = async () => {
      try {
        const diagnostic = await api.terminalStatus();
        if (cancelled) return;
        setPaymentTerminalState(diagnostic.connected ? "online" : "offline");
        setTerminalDiagnostic(diagnostic);
        setTerminalResult(diagnostic.connected ? "success" : "failed");
      } catch {
        if (!cancelled) setPaymentTerminalState("unavailable");
      } finally {
        if (!cancelled) pollTimer = setTimeout(pollTerminal, 5_000);
      }
    };

    void pollTerminal();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [api, isSubmittingRefund]);

  useEffect(() => {
    if (!api) return;
    void Promise.all([api.getConfig(), api.getCatalog()])
      .then(([configured, catalog]) => {
        if (configured) setSettings(configured);
        setItems(
          catalog.map((item) => ({
            ...item,
            id: crypto.randomUUID(),
            quantity: 0,
          })),
        );
      })
      .catch((error: unknown) => {
        setStatus({
          title: "Installation konnte nicht geladen werden",
          message:
            error instanceof Error ? error.message : "Unbekannter Fehler",
          detail: workerErrorDetail(error),
          tone: "warning",
        });
      });
  }, [api]);

  useEffect(() => {
    if (!api || !configurationComplete) return;
    let cancelled = false;

    setTerminalResult("testing");
    setTerminalDiagnostic(null);
    void api
      .terminalStatus()
      .then((diagnostic) => {
        if (cancelled) return;
        setTerminalDiagnostic(diagnostic);
        setTerminalResult(diagnostic.connected ? "success" : "failed");
      })
      .catch(() => {
        if (cancelled) return;
        setTerminalDiagnostic(null);
        setTerminalResult("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [api, configurationComplete]);

  useEffect(() => {
    const bridge = globalThis as unknown as { pos_getCurrentAccount?: unknown };
    if (typeof bridge.pos_getCurrentAccount !== "function") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refreshBalance = async () => {
      try {
        const account = await createLightspeedClient().getCurrentAccount();
        if (!cancelled)
          setBalance({
            totalCents: account.totalAmountMinor,
            paidCents: account.paidAmountMinor,
            currency: account.currency,
          });
      } catch {
        if (!cancelled) setBalance(null);
      } finally {
        if (!cancelled) timer = setTimeout(refreshBalance, 2_500);
      }
    };
    void refreshBalance();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  function updateQuantity(id: string, quantity: number) {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, quantity } : item)),
    );
  }

  async function saveSettings(
    nextSettings: RefundSettings,
    nextCatalog: CatalogDraft[],
  ): Promise<boolean> {
    if (!api) {
      setStatus({
        title: "Installation erforderlich",
        message: "Erstelle zuerst eine Installation in Lightspeed.",
        tone: "warning",
      });
      return false;
    }
    try {
      await api.saveSettings(nextSettings, nextCatalog);
      setSettings(nextSettings);
      setItems((current) =>
        nextCatalog.map((item) => {
          const existing = current.find(
            (candidate) => candidate.itemId === item.itemId,
          );
          return {
            ...item,
            id: existing?.id ?? crypto.randomUUID(),
            quantity: existing?.quantity ?? 0,
          };
        }),
      );
      setTerminalResult("idle");
      setTerminalDiagnostic(null);
      setStatus({
        title: "Einstellungen gespeichert",
        message: "Konfiguration und Katalog wurden im Worker persistiert.",
      });
      return true;
    } catch (error) {
      setStatus({
        title: "Speichern fehlgeschlagen",
        message: error instanceof Error ? error.message : "Unbekannter Fehler",
        detail: workerErrorDetail(error),
        tone: "warning",
      });
      return false;
    }
  }

  async function bootstrapInstallation() {
    try {
      const nextSession = await WorkerApi.bootstrap();
      const bridge = globalThis as unknown as { pos_storeData?: unknown };
      if (typeof bridge.pos_storeData !== "function")
        throw new Error("Lightspeed K-Series Speicher ist nicht verfügbar.");
      await createLightspeedClient().store(
        INSTALLATION_STORAGE_KEY,
        nextSession,
      );
      setSession(nextSession);
      setStatus({
        title: "Installation erstellt",
        message:
          "Die Zugangsdaten wurden im geschützten Lightspeed-Kassenspeicher abgelegt. Konfiguriere jetzt die Kasse.",
      });
    } catch (error) {
      setStatus({
        title: "Bootstrap fehlgeschlagen",
        message: error instanceof Error ? error.message : "Unbekannter Fehler",
        detail: workerErrorDetail(error),
        tone: "warning",
      });
    }
  }

  async function completeRefund() {
    if (!api) {
      setStatus({
        title: "Installation erforderlich",
        message: "Richte zuerst in den Einstellungen eine Installation ein.",
        tone: "warning",
      });
      setShowSettings(true);
      return;
    }
    if (!settings.settlementRoute.trim()) {
      setStatus({
        title: "Finanzweg erforderlich",
        message:
          "Hinterlege in den Einstellungen die Lightspeed Payment method ID für die Rückgabe.",
        tone: "warning",
      });
      setShowSettings(true);
      return;
    }
    setPaymentStage("preparing");
    setPaymentTerminalState("checking");
    setTerminalTransactionType(null);
    setIsSubmittingRefund(true);
    try {
      const bridge = globalThis as unknown as {
        pos_getCurrentAccount?: unknown;
      };
      if (typeof bridge.pos_getCurrentAccount !== "function")
        throw new Error(
          "Lightspeed K-Series bridge nicht gefunden. Ein Mock wird nur explizit im Entwicklungstest unterstützt.",
        );
      const orchestrator = createOrchestrator(true);
      const outcome = await orchestrator.start(
        items
          .filter((item) => item.quantity > 0)
          .map((item) => ({ itemId: item.itemId, quantity: item.quantity })),
        crypto.randomUUID(),
      );
      if (outcome.kind === "checkout") {
        setStatus({
          title: "Checkout erforderlich",
          message:
            "Die Rückgabeartikel sind im POS gebucht. Der verbleibende Bon kann jetzt regulär abgeschlossen werden.",
          detail: `Vorgang ${outcome.operation.id}`,
          tone: "warning",
        });
      } else if (outcome.kind === "blocked") {
        setStatus({
          title: "Rückgabe blockiert",
          message: outcome.reason,
          detail: outcome.operation
            ? `Vorgang ${outcome.operation.id}`
            : undefined,
          tone: "warning",
        });
      } else {
        const isPayment = outcome.operation.adyenTransactionType === "payment";
        setStatus({
          title: isPayment
            ? "Zahlung am Terminal abgeschlossen"
            : "Rückgabe am Terminal angenommen",
          message: isPayment
            ? "Der positive Restbetrag wurde als reguläre Adyen-Zahlung eingezogen und der Bon im POS finalisiert."
            : "Die Rückerstattung wurde idempotent beauftragt und im POS finalisiert. Die endgültige Adyen-Abrechnung wird per Webhook abgeglichen.",
          detail: `Vorgang ${outcome.operation.id} · ${selectedCount} Artikel`,
          tone: "success",
        });
        setItems((current) =>
          current.map((item) => ({ ...item, quantity: 0 })),
        );
      }
    } catch (error) {
      setStatus({
        title: "Rückgabe konnte nicht fortgesetzt werden",
        message: error instanceof Error ? error.message : "Unbekannter Fehler",
        detail: joinDetails(
          "Keine Terminaltransaktion wird automatisch erneut gesendet. Bereits im Bon gebuchte Pfandartikel nicht erneut auswählen; der aktuelle Bon kann anschließend direkt ausgeglichen werden.",
          workerErrorDetail(error),
        ),
        tone: "warning",
      });
    } finally {
      setIsSubmittingRefund(false);
    }
  }

  function createOrchestrator(clearSelectionAfterBooking = false) {
    if (!session) throw new Error("Installation fehlt.");
    return new RefundOrchestrator({
      pos: createLightspeedClient(),
      api: new DepositApiClient({
        installationId: session.installationId,
        installationSecret: session.installationSecret,
      }),
      depositItems: items
        .filter((item) => item.enabled)
        .map((item) => ({
          itemId: item.itemId,
          sku: item.sku,
          valueMinor: item.depositCents,
        })),
      paymentMethodId: settings.settlementRoute,
      maxRefundMinor: settings.maxRefundCents,
      onProgress: (stage) => {
        setPaymentStage(stage);
        if (clearSelectionAfterBooking && stage === "awaiting_terminal") {
          // The selected items are already durably journaled and booked in
          // Lightspeed at this point. Clear the UI selection even if the
          // terminal is declined, otherwise a retry would add the same items
          // to the table a second time and double the displayed amount.
          setItems((current) =>
            current.map((item) => ({ ...item, quantity: 0 })),
          );
        }
      },
      onTerminalTransaction: (transaction) =>
        setTerminalTransactionType(transaction.type),
    });
  }

  async function openOperations() {
    setShowOperations(true);
    setOperationsLoading(true);
    setOperationsError(undefined);
    if (!session) {
      setOperations([]);
      setOperationsLoading(false);
      setOperationsError("Richte zuerst eine Installation ein.");
      return;
    }
    try {
      const client = new DepositApiClient({
        installationId: session.installationId,
        installationSecret: session.installationSecret,
      });
      const [history, account] = await Promise.all([
        client.listOperations(),
        createLightspeedClient()
          .getCurrentAccount()
          .catch(() => null),
      ]);
      setOperations(history);
      setCurrentAccountId(account?.accountId);
    } catch (error) {
      setOperations([]);
      setOperationsError(
        error instanceof Error
          ? error.message
          : "Vorgänge konnten nicht geladen werden.",
      );
    } finally {
      setOperationsLoading(false);
    }
  }

  async function resumeRecovery(operation: RefundOperation) {
    setShowOperations(false);
    setPaymentStage("preparing");
    setPaymentTerminalState("checking");
    setTerminalTransactionType(null);
    setIsSubmittingRefund(true);
    try {
      const outcome = await createOrchestrator().resumeById(operation.id);
      if (outcome.kind === "blocked") {
        setStatus({
          title: "Manueller Abgleich erforderlich",
          message: outcome.reason,
          detail: `Vorgang ${operation.id}`,
          tone: "warning",
        });
      } else if (outcome.kind === "checkout") {
        setStatus({
          title: "Checkout geöffnet",
          message:
            "Die Pfandbuchung ist abgeschlossen; der verbleibende Bon kann regulär bezahlt werden.",
          detail: `Vorgang ${operation.id}`,
        });
      } else {
        setStatus({
          title: "Vorgang wiederhergestellt",
          message: "Der sichere Wiederanlauf wurde abgeschlossen.",
          detail: `Vorgang ${operation.id}`,
          tone: "success",
        });
      }
    } catch (error) {
      setStatus({
        title: "Wiederherstellung angehalten",
        message: error instanceof Error ? error.message : "Unbekannter Fehler",
        detail: joinDetails(
          `Vorgang ${operation.id}`,
          workerErrorDetail(error),
        ),
        tone: "warning",
      });
    } finally {
      setIsSubmittingRefund(false);
    }
  }

  function testTerminal() {
    if (!api) {
      setStatus({
        title: "Installation erforderlich",
        message: "Richte zuerst eine Installation ein.",
        tone: "warning",
      });
      return;
    }
    setTerminalResult("testing");
    setTerminalDiagnostic(null);
    void api
      .terminalStatus()
      .then((diagnostic) => {
        const copy = terminalDiagnosticCopy(diagnostic);
        setTerminalDiagnostic(diagnostic);
        setTerminalResult(diagnostic.connected ? "success" : "failed");
        setStatus({
          title: copy.title,
          message: `${copy.message} ${copy.action}`,
          detail: terminalDiagnosticDetails(diagnostic).join(" · "),
          tone: diagnostic.connected ? "success" : "warning",
        });
      })
      .catch((error) => {
        setTerminalResult("failed");
        setTerminalDiagnostic(null);
        setStatus({
          title: "Terminaltest fehlgeschlagen",
          message:
            error instanceof Error ? error.message : "Unbekannter Fehler",
          detail: workerErrorDetail(error),
          tone: "warning",
        });
      });
  }

  return (
    <main className="app-shell">
      {configurationComplete ? (
        <div className="screen-content">
          <div className="overview-grid">
            <section className="screen-section account-section">
              <p className="section-label">Aktueller Bon</p>
              <CheckoutPanel
                balance={balance}
                currency={settings.currency}
                isSubmitting={isSubmittingRefund}
                items={items}
                maxRefundCents={settings.maxRefundCents}
                settlementRoute={
                  isSubmittingRefund
                    ? "Rückgabe wird verarbeitet …"
                    : settings.settlementRoute
                }
                onComplete={() => {
                  void completeRefund();
                }}
              />
            </section>

            <section className="screen-section catalog-section">
              <div className="section-label-row">
                <p className="section-label">Pfand auswählen</p>
                <span
                  className={`terminal-state ${terminalResult === "failed" ? "failed" : ""}`}
                >
                  <i />
                  {terminalResult === "success"
                    ? "Terminal bereit"
                    : terminalResult === "testing"
                      ? "Terminal wird geprüft"
                      : terminalResult === "failed"
                        ? "Terminal nicht erreichbar"
                        : "Terminal ungeprüft"}
                </span>
              </div>

              <div className="guidance">
                <span>
                  Optional Pfand hinzufügen oder den vorhandenen Bon direkt mit
                  einer einzigen Bestätigung ausgleichen.
                </span>
              </div>

              <DepositCatalog
                currency={settings.currency}
                items={items}
                onAddSampleItems={() => setShowSettings(true)}
                onQuantityChange={updateQuantity}
              />
            </section>
          </div>
        </div>
      ) : (
        <section className="setup-screen">
          <SettingsIcon />
          <h1>Custos einrichten</h1>
          <p>
            Verbinde zuerst Lightspeed, das Adyen-Terminal und den Finanzweg für
            die Pfandrücknahme.
          </p>
          <button
            className="button primary"
            onClick={() => setShowSettings(true)}
            type="button"
          >
            Einstellungen öffnen
          </button>
        </section>
      )}

      <footer className="bottom-bar">
        <div className="bottom-actions">
          <button
            className="bar-button"
            type="button"
            onClick={() => {
              void openOperations();
            }}
          >
            <ReceiptIcon /> Vorgänge
          </button>
          <button
            className="bar-button"
            type="button"
            onClick={() => setShowSettings(true)}
          >
            <SettingsIcon /> Einstellungen
          </button>
        </div>
      </footer>

      <SettingsDialog
        catalog={items.map(({ id: _id, quantity: _quantity, ...item }) => item)}
        installationId={session?.installationId}
        open={showSettings}
        settings={settings}
        terminalResult={terminalResult}
        terminalDiagnostic={terminalDiagnostic}
        onBootstrap={bootstrapInstallation}
        onClose={() => setShowSettings(false)}
        onSave={saveSettings}
        onTerminalTest={testTerminal}
      />
      <OperationsDialog
        currentAccountId={currentAccountId}
        error={operationsError}
        loading={operationsLoading}
        open={showOperations}
        operations={operations}
        onClose={() => setShowOperations(false)}
        onResume={(operation) => {
          void resumeRecovery(operation);
        }}
      />
      <PaymentStatusDialog
        open={isSubmittingRefund}
        stage={paymentStage}
        terminalState={paymentTerminalState}
        transactionType={terminalTransactionType}
      />
      <StatusDialog
        open={status !== null}
        title={status?.title ?? ""}
        message={status?.message ?? ""}
        detail={status?.detail}
        tone={status?.tone}
        onClose={() => setStatus(null)}
      />
    </main>
  );
}

function isInstallationSession(value: unknown): value is InstallationSession {
  return (
    typeof value === "object" &&
    value !== null &&
    "installationId" in value &&
    "installationSecret" in value &&
    typeof value.installationId === "string" &&
    typeof value.installationSecret === "string"
  );
}

function workerErrorDetail(error: unknown): string | undefined {
  if (!(error instanceof WorkerRequestError)) return undefined;
  return joinDetails(
    error.status ? `HTTP ${error.status}` : undefined,
    error.code ? `Fehlercode: ${error.code}` : undefined,
    error.requestId ? `Worker-Request-ID: ${error.requestId}` : undefined,
  );
}

function joinDetails(...parts: Array<string | undefined>): string | undefined {
  const details = parts.filter((part): part is string => Boolean(part));
  return details.length > 0 ? details.join(" · ") : undefined;
}
